import { createRoute, redirect, useBlocker } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Loader2, MessageCircle, Send } from "lucide-react";
import { generatePublicId } from "@carry/shared";
import {
  NEWS_BODY_MAX_LENGTH,
  type NewsTargetRoleDto,
  type PreviewNewsSendOutputDto,
} from "@carry/contract";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
} from "@carry/ui";
import { Route as RootRoute } from "./__root";
import { isUnauthorizedError, notifyAuthSessionExpired } from "../lib/auth-session";
import { useAdminApi } from "../providers/api-context";
import type { RouterContext } from "../router-context";

export function requireNewsRoute({ context }: { context: RouterContext }) {
  if (context.authLoading) return;
  if (!context.session) throw redirect({ to: "/auth/login" });
  if (context.session.user.role !== "admin" && context.session.user.role !== "office_staff") {
    throw redirect({ to: "/" });
  }
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/news",
  beforeLoad: requireNewsRoute,
  component: NewsPage,
});

const ROLE_OPTIONS: ReadonlyArray<{ value: NewsTargetRoleDto; label: string }> = [
  { value: "admin", label: "管理者" },
  { value: "office_staff", label: "事務員" },
  { value: "driver", label: "ドライバー" },
];

const ROLE_LABELS = Object.fromEntries(
  ROLE_OPTIONS.map(({ value, label }) => [value, label]),
) as Record<NewsTargetRoleDto, string>;

const EXCLUDED_REASON_LABELS: Record<string, string> = {
  deleted: "削除済み",
  inactive: "利用停止中",
  inactive_user: "利用停止中",
  line_unlinked: "LINE未連携",
  line_notifications_disabled: "LINE通知オフ",
  line_not_linked: "LINE未連携",
  not_line_linked: "LINE未連携",
  missing_line_user_id: "LINE未連携",
};

const LEAVE_CONFIRM_MESSAGE = "入力中の内容は送信されていません。ページを離れますか？";

type FormValues = {
  title: string;
  body: string;
  target_roles: NewsTargetRoleDto[];
};

type Confirmation = {
  requestId: string;
  values: FormValues;
  preview: PreviewNewsSendOutputDto;
};

const EMPTY_VALUES: FormValues = { title: "", body: "", target_roles: [] };

function errorCode(error: unknown): unknown {
  if (typeof error !== "object" || error === null) return undefined;
  if (
    "data" in error &&
    typeof error.data === "object" &&
    error.data !== null &&
    "code" in error.data
  ) {
    return error.data.code;
  }
  if ("code" in error) return error.code;
  return undefined;
}

function NewsPage() {
  const api = useAdminApi();
  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [sendPending, setSendPending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const hasUnsentInput =
    !sent &&
    (values.title.trim().length > 0 ||
      values.body.trim().length > 0 ||
      values.target_roles.length > 0);

  const shouldBlockNavigation = useCallback(async () => {
    if (!hasUnsentInput) return false;
    return !window.confirm(LEAVE_CONFIRM_MESSAGE);
  }, [hasUnsentInput]);

  useBlocker({
    shouldBlockFn: shouldBlockNavigation,
    enableBeforeUnload: false,
    disabled: !hasUnsentInput,
  });

  useEffect(() => {
    if (!hasUnsentInput) return;
    const preventUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [hasUnsentInput]);

  const updateValues = (next: FormValues) => {
    setValues(next);
    setSent(false);
    setFormError(null);
    setSendError(null);
    setConfirmation(null);
  };

  const canPreview =
    values.target_roles.length > 0 &&
    values.title.trim().length > 0 &&
    values.body.trim().length > 0;

  const previewSend = async () => {
    if (!canPreview || previewPending) return;
    setPreviewPending(true);
    setFormError(null);
    const requestId = generatePublicId();
    const snapshot: FormValues = {
      title: values.title.trim(),
      body: values.body.trim(),
      target_roles: [...values.target_roles],
    };
    try {
      const preview = await api.news.previewSend({ target_roles: snapshot.target_roles });
      setConfirmation({ requestId, values: snapshot, preview });
    } catch (error) {
      if (isUnauthorizedError(error)) {
        notifyAuthSessionExpired();
      } else {
        setFormError("送信対象者を確認できませんでした。時間をおいて再度お試しください");
      }
    } finally {
      setPreviewPending(false);
    }
  };

  const sendMessage = async () => {
    if (
      confirmation === null ||
      sendPending ||
      confirmation.preview.recipient_count === 0 ||
      !confirmation.preview.line_push_available
    ) {
      return;
    }
    setSendPending(true);
    setSendError(null);
    try {
      await api.news.send({
        request_id: confirmation.requestId,
        title: confirmation.values.title,
        body: confirmation.values.body,
        target_roles: confirmation.values.target_roles,
        recipient_public_ids: confirmation.preview.recipients.map(({ public_id }) => public_id),
      });
      setConfirmation(null);
      setSent(true);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        notifyAuthSessionExpired();
      } else if (errorCode(error) === "RECIPIENTS_CHANGED") {
        setConfirmation(null);
        setFormError("送信対象者が変わりました。内容を再確認してください");
      } else {
        setSendError("送信に失敗しました。入力内容を保持しています。もう一度お試しください");
      }
    } finally {
      setSendPending(false);
    }
  };

  const continueSending = () => {
    setValues(EMPTY_VALUES);
    setConfirmation(null);
    setFormError(null);
    setSendError(null);
    setSent(false);
  };

  if (sent) {
    return (
      <main style={{ maxWidth: 720, margin: "0 auto", padding: "32px 0" }}>
        <section
          style={{
            background: "var(--bg-raised)",
            border: "0.5px solid var(--rule)",
            borderRadius: 4,
            padding: 32,
            textAlign: "center",
          }}
        >
          <MessageCircle
            size={32}
            aria-hidden="true"
            style={{ margin: "0 auto 12px", color: "var(--success)" }}
          />
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>送信処理を開始しました</h1>
          <p style={{ color: "var(--ink-2)", fontSize: 13, marginBottom: 24 }}>
            LINEへの送信結果はLINEログで確認できます。
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
            <a
              href="/line-logs"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 36,
                padding: "0 16px",
                borderRadius: 4,
                background: "var(--primary)",
                color: "var(--primary-foreground)",
                fontSize: 14,
                fontWeight: 500,
                textDecoration: "none",
              }}
            >
              LINEログで結果を確認
            </a>
            <Button type="button" variant="outline" onClick={continueSending}>
              続けて送る
            </Button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main style={{ width: "min(100%, 720px)", margin: "0 auto", padding: "8px 0 32px" }}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>LINEメッセージ送信</h1>
        <p style={{ fontSize: 13, color: "var(--ink-2)" }}>
          送信先と内容を入力し、対象者を確認してからLINEへ送信します。
        </p>
      </header>

      <section
        style={{
          background: "var(--bg-raised)",
          border: "0.5px solid var(--rule)",
          borderRadius: 4,
          padding: "clamp(16px, 4vw, 28px)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              送信先{" "}
              <span aria-hidden="true" style={{ color: "var(--danger)" }}>
                *
              </span>
            </legend>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
              {ROLE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={values.target_roles.includes(option.value)}
                    onChange={() => {
                      const target_roles = values.target_roles.includes(option.value)
                        ? values.target_roles.filter((role) => role !== option.value)
                        : [...values.target_roles, option.value];
                      updateValues({ ...values, target_roles });
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {values.target_roles.length === 0 && (
              <p style={{ color: "var(--ink-3)", fontSize: 11, marginTop: 6 }}>
                1つ以上選択してください
              </p>
            )}
          </fieldset>

          <div>
            <label
              htmlFor="news-title"
              style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}
            >
              タイトル{" "}
              <span aria-hidden="true" style={{ color: "var(--danger)" }}>
                *
              </span>
            </label>
            <Input
              id="news-title"
              value={values.title}
              onChange={(event) => updateValues({ ...values, title: event.target.value })}
              placeholder="タイトルを入力"
              maxLength={200}
            />
          </div>

          <div>
            <label
              htmlFor="news-body"
              style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}
            >
              メッセージ本文{" "}
              <span aria-hidden="true" style={{ color: "var(--danger)" }}>
                *
              </span>
            </label>
            <Textarea
              id="news-body"
              value={values.body}
              onChange={(event) => updateValues({ ...values, body: event.target.value })}
              placeholder="メッセージ本文を入力"
              maxLength={NEWS_BODY_MAX_LENGTH}
              style={{ minHeight: 180, resize: "vertical" }}
            />
          </div>

          {formError && (
            <p role="alert" style={{ color: "var(--danger)", fontSize: 12 }}>
              {formError}
            </p>
          )}

          <Button
            type="button"
            onClick={() => void previewSend()}
            disabled={!canPreview || previewPending}
          >
            {previewPending ? <Loader2 size={15} className="animate-spin" /> : null}
            内容を確認
          </Button>
        </div>
      </section>

      <Dialog
        open={confirmation !== null}
        onOpenChange={(open) => {
          if (!open && !sendPending) setConfirmation(null);
        }}
      >
        {confirmation && (
          <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>送信内容の確認</DialogTitle>
              <DialogDescription>内容と送信対象者を確認してください。</DialogDescription>
            </DialogHeader>

            <dl style={{ display: "grid", gap: 14, fontSize: 13 }}>
              <div>
                <dt style={{ color: "var(--ink-3)", fontSize: 11, marginBottom: 3 }}>送信先区分</dt>
                <dd>
                  {confirmation.values.target_roles.map((role) => ROLE_LABELS[role]).join("、")}
                </dd>
              </div>
              <div>
                <dt style={{ color: "var(--ink-3)", fontSize: 11, marginBottom: 3 }}>タイトル</dt>
                <dd style={{ overflowWrap: "anywhere" }}>{confirmation.values.title}</dd>
              </div>
              <div>
                <dt style={{ color: "var(--ink-3)", fontSize: 11, marginBottom: 3 }}>
                  メッセージ本文
                </dt>
                <dd style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {confirmation.values.body}
                </dd>
              </div>
              <div>
                <dt style={{ color: "var(--ink-3)", fontSize: 11, marginBottom: 3 }}>送信対象</dt>
                <dd>{confirmation.preview.recipient_count}人</dd>
              </div>
              {confirmation.preview.excluded_count > 0 && (
                <div>
                  <dt style={{ color: "var(--ink-3)", fontSize: 11, marginBottom: 3 }}>
                    対象外 {confirmation.preview.excluded_count}人
                  </dt>
                  <dd>
                    {Object.entries(confirmation.preview.excluded_counts)
                      .filter(([, count]) => count > 0)
                      .map(([reason, count]) => (
                        <div key={reason}>
                          {EXCLUDED_REASON_LABELS[reason] ?? "その他"}: {count}人
                        </div>
                      ))}
                  </dd>
                </div>
              )}
            </dl>

            {confirmation.preview.recipients.length > 0 && (
              <ul
                aria-label="送信対象者"
                style={{
                  maxHeight: 240,
                  overflowY: "auto",
                  border: "0.5px solid var(--rule)",
                  borderRadius: 3,
                  padding: 0,
                  margin: 0,
                  listStyle: "none",
                }}
              >
                {confirmation.preview.recipients.map((recipient, index) => (
                  <li
                    key={recipient.public_id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "9px 12px",
                      borderBottom:
                        index < confirmation.preview.recipients.length - 1
                          ? "0.5px solid var(--rule-soft)"
                          : undefined,
                      fontSize: 12,
                    }}
                  >
                    <span style={{ overflowWrap: "anywhere" }}>{recipient.name}</span>
                    <span style={{ color: "var(--ink-3)", flexShrink: 0 }}>
                      {ROLE_LABELS[recipient.role]}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {!confirmation.preview.line_push_available && (
              <p role="alert" style={{ color: "var(--danger)", fontSize: 12 }}>
                現在LINEメッセージを送信できません。
              </p>
            )}
            {confirmation.preview.recipient_count === 0 && (
              <p role="alert" style={{ color: "var(--danger)", fontSize: 12 }}>
                送信できる対象者がいません。
              </p>
            )}
            {sendError && (
              <p role="alert" style={{ color: "var(--danger)", fontSize: 12 }}>
                {sendError}
              </p>
            )}
            <p style={{ color: "var(--danger)", fontSize: 12, fontWeight: 600 }}>
              送信後は取り消せません
            </p>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmation(null)}
                disabled={sendPending}
              >
                戻る
              </Button>
              <Button
                type="button"
                onClick={() => void sendMessage()}
                disabled={
                  sendPending ||
                  confirmation.preview.recipient_count === 0 ||
                  !confirmation.preview.line_push_available
                }
              >
                {sendPending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {confirmation.preview.recipient_count}人にLINEで送信する
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </main>
  );
}
